import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import useSettings from '../../hooks/useSettings'
import api from '../../services/api'

// COMPONENTE EXCLUSIVO PARA NOTIFICAÇÕES DE TICKETS PENDENTES/RESETADOS
// Este componente verifica o campo 'foco' do usuário - só notifica se foco estiver ativo
// Notificações de NOVAS MENSAGENS não passam por aqui e sempre funcionam
const PendingTicketsNotifier = () => {
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const { getAll: getAllSettings } = useSettings()
  const [serverAccessCheckDone, setServerAccessCheckDone] = useState(false)
  const [serverAllowsTicketAccess, setServerAllowsTicketAccess] = useState(false)

  // Log para debug do campo foco
  // useEffect(() => {
  //   console.log('PendingTicketsNotifier: Usuário carregado/atualizado:', {
  //     id: user?.id,
  //     name: user?.name,
  //     foco: user?.foco,
  //     typeof_foco: typeof user?.foco
  //   })
  // }, [user?.id, user?.foco])

  const [chatbotNotificationReset, setChatbotNotificationReset] = useState('0')
  const intervalRef = useRef(null)
  const lastNotificationRef = useRef(0)
  const processedTicketsRef = useRef(new Map()) // ticketId -> updatedAt

  // Função auxiliar para verificar se o foco está ativo
  const isFocoAtivo = useCallback(() => {
    const userFoco = user?.foco
    // Debug detalhado da verificação do foco
    // console.log('PendingTicketsNotifier: Verificação de foco detalhada:', {
    //   'user existe': !!user,
    //   'user.foco valor': userFoco,
    //   'user.foco tipo': typeof userFoco,
    //   'user.foco === true': userFoco === true,
    //   'user.foco === false': userFoco === false,
    //   'user.foco === undefined': userFoco === undefined,
    //   'user.foco === null': userFoco === null
    // })

    // Se o campo foco não existir ou for undefined/null, considera como false (sem foco)
    if (userFoco === undefined || userFoco === null) {
      console.log('PendingTicketsNotifier: Foco indefinido, retornando false')
      return false
    }

    // Aceita apenas valores verdadeiramente true
    const resultado = userFoco === true
    // console.log('PendingTicketsNotifier: Resultado da verificação de foco:', resultado)
    return resultado
  }, [user?.foco])

  useEffect(() => {
    const checkTicketAccess = async () => {
      if (!user?.id) {
        setServerAccessCheckDone(false)
        setServerAllowsTicketAccess(false)
        return
      }

      try {
        await api.get('/auth/me')
        setServerAllowsTicketAccess(true)
      } catch (error) {
        if (error?.response?.status === 403) {
          setServerAllowsTicketAccess(false)
          return
        }

        if (error?.response?.status !== 401) {
          console.error(
            'Erro ao validar acesso do usuário às notificações de tickets pendentes:',
            error
          )
        }
      } finally {
        setServerAccessCheckDone(true)
      }
    }

    checkTicketAccess()
  }, [user?.id])

  // Carregar configuração do chatbotNotificationReset
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getAllSettings()
        const chatbotSetting = settings.find(s => s.key === 'chatbotNotificationReset')
        const resetValue = chatbotSetting?.value || '0'
        setChatbotNotificationReset(resetValue)

        // console.log('PendingTicketsNotifier: chatbotNotificationReset =', resetValue, 'minutos')
      } catch (error) {
        // Ignorar erros 401 que ocorrem durante logout
        if (error?.response?.status !== 401) {
          console.error('Erro ao buscar configurações de notificação:', error)
        }
      }
    }

    if (user?.id) {
      fetchSettings()
    }
  }, [getAllSettings, user?.id])

  // Função para buscar tickets pendentes
  const fetchPendingTickets = useCallback(async () => {
    if (!serverAccessCheckDone || !serverAllowsTicketAccess) {
      return []
    }

    try {
      const response = await api.get('/tickets/', {
        params: {
          status: 'pending',
          showAll: 'true',
          pageNumber: 1
        }
      })

      const tickets = response.data.records || response.data.tickets || []

      // Filtrar tickets relevantes para o usuário
      const relevantTickets = tickets.filter(ticket => {
        // Não incluir tickets de chatbot
        if (ticket.chatbot) return false

        // Admin vê todos os tickets pendentes
        if (user.profile === 'admin') return true

        // Usuários veem tickets atribuídos a eles ou sem atribuição nas suas filas
        if (ticket.userId === user.id) return true

        // Tickets sem usuário atribuído nas filas do usuário
        if (!ticket.userId && user.queues) {
          return user.queues.some(queue => queue.id === ticket.queueId)
        }

        return false
      })

      return relevantTickets
    } catch (error) {
      if (error?.response?.status === 403) {
        setServerAllowsTicketAccess(false)
        return []
      }

      // Ignorar erros 401 que ocorrem durante logout
      if (error?.response?.status !== 401) {
        console.error('Erro ao buscar tickets pendentes:', error)
      }
      return []
    }
  }, [serverAccessCheckDone, serverAllowsTicketAccess, user?.id, user?.profile, user?.queues])

  // Função para atualizar lista de tickets pendentes
  const updatePendingTickets = useCallback(async () => {
    const tickets = await fetchPendingTickets()
    return tickets
  }, [fetchPendingTickets])

  // Configurar o intervalo de notificações
  useEffect(() => {
    // console.log('PendingTicketsNotifier: useEffect executado com dependências:', {
    //   chatbotNotificationReset,
    //   userId: user?.id,
    //   userProfile: user?.profile,
    //   userFoco: user?.foco,
    //   userQueuesLength: user?.queues?.length,
    //   socketManager: !!socketManager
    // })

    // Limpar intervalo anterior se existir
    if (intervalRef.current) {
      // console.log('PendingTicketsNotifier: Limpando intervalo anterior')
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    const resetMinutes = parseInt(chatbotNotificationReset)

    // Se o valor for 0 ou inválido, não configurar notificações
    if (!resetMinutes || resetMinutes <= 0) {
      // console.log('PendingTicketsNotifier: Notificações desabilitadas (valor 0 ou inválido)')
      return
    }

    if (!serverAccessCheckDone || !serverAllowsTicketAccess) {
      return
    }

    // VERIFICAÇÃO DE FOCO: APENAS para notificações de tickets pendentes/resetados
    // Notificações de novas mensagens NÃO passam por aqui e sempre funcionam
    if (!isFocoAtivo()) {
      // console.log('✋ PendingTicketsNotifier: NOTIFICAÇÕES DE RESET DESABILITADAS - usuário sem foco ativo')
      // console.log('ℹ️  PendingTicketsNotifier: Notificações de NOVAS MENSAGENS continuam funcionando normalmente')
      return
    }

    // console.log('✅ PendingTicketsNotifier: NOTIFICAÇÕES DE RESET HABILITADAS - usuário com foco ativo')
    // console.log('ℹ️  PendingTicketsNotifier: Notificações de NOVAS MENSAGENS sempre funcionam (independente do foco)')

    // Verificar se o usuário está logado
    if (!user?.id) {
      // console.log('PendingTicketsNotifier: Usuário não encontrado')
      return
    }

    // Converter minutos para milissegundos
    const intervalMs = resetMinutes * 60 * 1000

    // console.log(`PendingTicketsNotifier: Configurando notificações a cada ${resetMinutes} minutos`)
    // console.log(`PendingTicketsNotifier: Usuário com foco ativo - user.foco =`, user?.foco, 'user.id =', user?.id)

    // Função para notificar tickets pendentes através do sistema de notificações
    const sendPendingTicketsNotification = async () => {
      // Verificação adicional de segurança antes de processar NOTIFICAÇÕES DE RESET
      if (!isFocoAtivo()) {
        // console.log('✋ PendingTicketsNotifier: NOTIFICAÇÃO DE RESET CANCELADA - usuário sem foco ativo no momento da execução')
        // console.log('ℹ️  PendingTicketsNotifier: Notificações de NOVAS MENSAGENS não são afetadas por esta verificação')
        return
      }

      const tickets = await updatePendingTickets()

      if (tickets.length > 0) {
        // console.log(`PendingTicketsNotifier: Encontrados ${tickets.length} tickets pendentes relevantes`)

        // Para cada ticket pendente relevante, emitir evento do tipo que o NotificationsCenter reconhece
        const currentTicketIds = new Set()
        tickets.forEach(ticket => {
          const isForCurrentUser = ticket.userId === user.id || (!ticket.userId && user.queues?.some(queue => queue.id === ticket.queueId))
          const lastProcessedUpdatedAt = processedTicketsRef.current.get(ticket.id)
          const ticketUpdatedAt = ticket.updatedAt
          currentTicketIds.add(ticket.id)

          // Só notifica repetição se ainda houver mensagens não visualizadas
          if (
            (isForCurrentUser || user.profile === 'admin') &&
            lastProcessedUpdatedAt !== ticketUpdatedAt &&
            (ticket.unreadMessages || 0) > 0
          ) {
            // console.log(`PendingTicketsNotifier: Processando ticket pendente #${ticket.id}`)

            // Marcar como processado para evitar duplicatas enquanto o ticket não mudar
            processedTicketsRef.current.set(ticket.id, ticketUpdatedAt)

            // Disparar evento local para o NotificationsCenter processar a repetição.
            setTimeout(() => {
              try {
                const timestamp = new Date().toISOString()
                window.dispatchEvent(
                  new CustomEvent('pending-ticket-notification', {
                    detail: {
                      action: 'pending-ticket-notification',
                      source: 'pending-tickets-notifier',
                      ticket: {
                        ...ticket,
                        status: 'pending',
                        unreadMessages: Math.max(ticket.unreadMessages || 0, 1),
                        notificationCycleKey: `local-${ticket.id}-${timestamp}`
                      },
                      message: {
                        type: 'pending-notification',
                        content: `Ticket #${ticket.id} (${ticket.contact?.name || 'Sem nome'}) está pendente com ${Math.max(ticket.unreadMessages || 0, 1)} mensagem${Math.max(ticket.unreadMessages || 0, 1) > 1 ? 's' : ''} não lida${Math.max(ticket.unreadMessages || 0, 1) > 1 ? 's' : ''}`,
                        timestamp
                      }
                    }
                  })
                )

                // console.log(`PendingTicketsNotifier: Evento emitido para ticket #${ticket.id}`)
              } catch (error) {
                console.error('Erro ao disparar evento local de ticket pendente:', error)
              }
            }, 100 * Math.random()) // Pequeno delay randômico para evitar spam
          }
        })

        // Remove do cache tickets que não estão mais pendentes
        for (const ticketId of processedTicketsRef.current.keys()) {
          if (!currentTicketIds.has(ticketId)) {
            processedTicketsRef.current.delete(ticketId)
          }
        }

        lastNotificationRef.current = Date.now()
      } else {
        console.log('PendingTicketsNotifier: Nenhum ticket pendente encontrado')
      }
    }

    // Função para envio de notificação
    const notifyPendingTickets = async () => {
      // Usar a mesma verificação robusta do campo foco
      if (user?.id && isFocoAtivo()) {
        await sendPendingTicketsNotification()
      }
      // else {
      //   console.log('PendingTicketsNotifier: Notificação ignorada - usuário sem ID ou foco inativo')
      // }
    }

    // Fazer primeira verificação após 30 segundos (para não sobrecarregar o login)
    const initialTimeout = setTimeout(notifyPendingTickets, 30000)

    // Configurar intervalo regular
    intervalRef.current = setInterval(notifyPendingTickets, intervalMs)

    // Limpar timeouts e intervalos quando o componente for desmontado ou dependências mudarem
    return () => {
      clearTimeout(initialTimeout)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [
    chatbotNotificationReset,
    isFocoAtivo,
    serverAccessCheckDone,
    serverAllowsTicketAccess,
    socketManager,
    updatePendingTickets,
    user?.foco,
    user?.id,
    user?.profile,
    user?.queues
  ])

  // Escutar mudanças em tickets via socket para limpar cache quando necessário
  useEffect(() => {
    if (!user?.id || !socketManager) return

    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const handleTicketUpdate = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        // Limpar cache quando houver mudanças para reprocessar notificações
        if (data.ticket && (data.ticket.status === 'pending' || data.ticket.status === 'open' || data.ticket.status === 'closed')) {
          processedTicketsRef.current.delete(data.ticket.id)
        }
      } else if (data.action === 'delete') {
        // Remover ticket do cache se foi deletado
        processedTicketsRef.current.delete(data.ticketId)
      }
    }

    socket.on(`company-${companyId}-ticket`, handleTicketUpdate)

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicketUpdate)
    }
  }, [user?.id, socketManager])

  // Este componente não renderiza nada visualmente, apenas monitora e notifica
  return null
}

export default PendingTicketsNotifier